import { useDispatch, useSelector } from "react-redux";
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormLabel,
  FormControl,
  TextField,
  Typography,
  Grid2,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  getContentTypeForEntry,
  getEntryForEdit,
  submitFormData,
  updateEntryData,
} from "../../actions/entryActions";
import AceEditor from "react-ace";

import "ace-builds/webpack-resolver";
import "ace-builds/src-noconflict/theme-tomorrow";
import "ace-builds/src-noconflict/mode-json";

import { useParams } from "react-router-dom";
import RichText from "../../components/richText/RichText";

import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import RightDrawer from "../../components/RightDrawer";
import AddReferrenceModal from "./AddReferenceModal";
import AddButton from "../../themes/overrides/AddButton";
import RemoveIcon from "../../components/icons/RemoveIcon";

const EditEntry = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const data = useSelector((state) => state.entry.entryFields);
  const entry = useSelector((state) => state.entry.entry);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRef, setselectedRef] = useState("");

  const spaceLocales = ["en", "es"];
  const primaryLocale = ["en"];

  // State for dynamic fields, map by field ids
  const [fields, setFields] = useState({});

  const { workspaceId, id } = useParams();

  const lastFetchedId = React.useRef(null);

  useEffect(() => {
    if (workspaceId && id && lastFetchedId.current !== id) {
      lastFetchedId.current = id;
      dispatch(getEntryForEdit(workspaceId, id));
    }
  }, [dispatch, workspaceId, id]);

  useEffect(() => {
    if (entry?.contentTypeId) {
      dispatch(getContentTypeForEntry(workspaceId, entry.contentTypeId));
    }
  }, [dispatch, workspaceId, entry?.contentTypeId]);

  useEffect(() => {
    if (entry) {
      setFormData(entry);
      setSelectedVersion(entry.version || (entry.versionData ? Object.keys(entry.versionData).length : 1));
      setLoading(false);
    } else if (entry === null) {
      setLoading(false);
    }
  }, [entry]);

  // Initialize fields for array type fields
  useEffect(() => {
    const initialFields = {};
    data.forEach((field) => {
      if (field.type === "array") {
        initialFields[field.id] = [""]; // Initialize with one empty field for array fields
      }
    });
    setFields(initialFields);
    setLoading(false);
  }, [data]);

  // const handleArrayChange = (id, index, value) => {
  //   console.log(fields);
  //   setFields((prevFields) => {
  //     const updatedArray = [...(prevFields[id] || [])];
  //     updatedArray[index] = value;

  //     if (index === updatedArray.length - 1 && value) {
  //       updatedArray.push(""); // Add new empty field if typing in the last one
  //     }

  //     const fieldValues = { ...prevFields, [id]: updatedArray };
  //     const arr = fieldValues[id].filter((value) => value !== "");
  //     handleChange(id, arr);

  //     return fieldValues;
  //   });
  // };

  const handleArrayChange = (id, index, value) => {
    setFormData((prevData) => {
      const currentArray = prevData.data?.[id] || []; // Safely get the current array
      const updatedArray = [...currentArray];

      // Update the value at the specific index
      updatedArray[index] = value;

      // Add a new empty field if the user is typing in the last field and value is not empty
      if (index === updatedArray.length - 1 && value.trim() !== "") {
        updatedArray.push("");
      }

      // Return the updated state
      return {
        ...prevData,
        data: {
          ...prevData.data,
          [id]: updatedArray,
        },
      };
    });
  };

  function safeParseJSON(value) {
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error("Invalid JSON string:", error);
      return value; // or handle in another way
    }
  }

  const handlePublish = async (status) => {
    const updateBody = { published: status };
    try {
      const response = await dispatch(updateEntryData(id, updateBody));
      if (response.status == "success") {
      }
    } catch (error) {
      console.error("Error:", error); // Handle any errors here
    }
  };

  const [selectedVersion, setSelectedVersion] = useState(entry?.version); //

  const versionData = entry?.versionData;

  const handleVersionDataChange = (event) => {
    setSelectedVersion(event.target.value); // Update selected version
  };
  const openModal = () => {
    setModalOpen(true);
  }

  const handleRefChange = (value, fieldId) => {
    setselectedRef(value);
    setFormData((prevData) => ({
      ...prevData,
      [fieldId]: value,
    }));
  };

  const removeSelectedRef = (fieldId) => {
    setselectedRef(""); // Clear the selected reference display
    setFormData((prevData) => ({
      ...prevData,
      [fieldId]: null, // Remove the reference from formData
    }));
  };
  const renderInput = (field) => {
    const { id, name, type, required, localizations } = field;

    let locales = localizations ? spaceLocales : primaryLocale;

    switch (type) {
      case "number":
        return (
          <FormControl fullWidth variant="outlined" component="fieldset">
            <FormLabel component="legend">{name}</FormLabel>

            <TextField
              type="number"
              variant="outlined"
              fullWidth
              // value={entry.data[id]}
              value={formData.data?.[id] || ""}
              required={required}
              onChange={(e) => handleChange(id, parseInt(e.target.value))}
            />
          </FormControl>
        );

      case "string":
        return (
          <FormControl fullWidth variant="outlined" component="fieldset">
            <FormLabel component="legend">{name}</FormLabel>
            {locales.map((locale) => (
              <TextField
                key={`${id}-${locale}`}
                type="text"
                label={`(${locale})`}
                variant="outlined"
                fullWidth
                // value={entry.data.localeContent[locale][id]}
                // value={formData.data.localeContent[locale][id] || ""}
                value={formData?.data?.localeContent?.[locale]?.[id] || ""}
                style={{ marginBottom: "3px" }}
                required={required}
                onChange={(e) => handleChange(id, e.target.value, locale)}
              />
            ))}
          </FormControl>
        );

      case "richText":
        return (
          <FormControl fullWidth variant="outlined" component="fieldset">
            <FormLabel component="legend">{name}</FormLabel>
            {localizations ? (
              locales.map((locale) => (
                <div>
                  <RichText
                    key={`${id}-${locale}`}
                    label={`(${locale})`}
                    type="richText"
                    value={formData.data?.localeContent[locale][id]}
                    onChange={(value) => handleChange(id, value, locale)}
                  />
                </div>
              ))
            ) : (
              <div>
                <RichText
                  type="richText"
                  value={formData.data.localeContent["en"][id]}
                  onChange={(value) => handleChange(id, value)}
                />
              </div>
            )}
          </FormControl>
        );

      case "object":
        return (
          <AceEditor
            mode="json"
            theme="tomorrow"
            value={JSON.stringify(formData.data?.[id]) || ""}
            // value={formData.localeContent[locale][id] || ""}
            onChange={(value) => handleChange(id, safeParseJSON(value))}
            editorProps={{ $blockScrolling: true }}
            style={{ width: "100%", minHeight: "100px" }}
          />
        );

      // case "array":
      //   return (
      //     <FormControl fullWidth variant="outlined" component="fieldset">
      //       <FormLabel component="legend">{name}</FormLabel>
      //       {(formData.data[id] || []).map((field, index) => (
      //         <TextField
      //           key={index}
      //           type="text"
      //           variant="outlined"
      //           fullWidth
      //           required={required}
      //           style={{ marginBottom: "10px" }}
      //           value={field}
      //           onChange={(e) => handleArrayChange(id, index, e.target.value)}
      //         />
      //       ))}
      //     </FormControl>
      //   );
      case "array":
        return (
          <FormControl fullWidth variant="outlined" component="fieldset">
            <FormLabel component="legend">{name}</FormLabel>
            {(formData.data?.[id] || [""]).map((value, index) => (
              <TextField
                key={`${id}-${index}`}
                type="text"
                variant="outlined"
                fullWidth
                required={required}
                style={{ marginBottom: "10px" }}
                value={value || ""}
                onChange={(e) => handleArrayChange(id, index, e.target.value)}
              />
            ))}
          </FormControl>
        );
      case "reference":
        return (
          <>
            <Typography>{name}</Typography>
            {
              selectedRef ? (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  sx={{
                    width: '100%',
                    marginLeft: 0,
                    borderWidth: 1,
                    borderColor: "#E6E6E6",
                    borderStyle: "solid",
                    marginRight: 0,
                    alignItems: 'flex-start',
                    p: 1,
                    borderRadius: 2
                  }}>
                  <Typography variant="body1">
                    {selectedRef}
                  </Typography>
                  <IconButton
                    color="secondary"
                    onClick={() => removeSelectedRef(selectedRef)}
                    aria-label="delete"
                  >
                    <RemoveIcon />
                  </IconButton>
                </Box>
              ) :
                <Box
                  sx={{
                    width: '100%',
                    marginLeft: 0,
                    borderWidth: 1,
                    borderColor: "#E6E6E6",
                    borderStyle: "solid",
                    marginRight: 0,
                    alignItems: 'flex-start',
                    p: 2,
                    borderRadius: 2
                  }}
                  display="flex" justifyContent="space-evenly">
                  <Button
                    variant="outlined"
                    label="Add Existing Reference "
                    onClick={openModal}
                  >+ Add Existing Reference</Button>
                  <RightDrawer />
                </Box>
            }

            <AddReferrenceModal
              isOpen={modalOpen}
              onClose={() => {
                setModalOpen(false)
              }}
              // onChange={handleRefChange}
              onChange={(value) => handleRefChange(value, name)}
            />
          </>
        );

      default:
        return null;
    }
  };

  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   if (formData){
  //     const localeData = formData.data.localeContent;
  //     delete formData.data.localeContent;

  //     const formattedData = {
  //       identifier: formData.identifier,
  //       data: formData.data,
  //       localeData: localeData,
  //     };

  //     dispatch(updateEntryData(id, formattedData))

  //     console.log("Formatted Data:", formattedData);
  //   }

  // };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log(formData);

    if (formData) {
      const { localeContent, ...restData } = formData.data; // Extract `localeContent` immutably

      const formattedData = {
        identifier: formData.identifier,
        data: restData, // Use the new object without `localeContent`
        localeData: localeContent, // Keep `localeContent` separately
      };

      dispatch(updateEntryData(id, formattedData));

      console.log("Formatted Data:", formattedData);
    }
  };

  const handleChange = (id, value, locale = null) => {
    setFormData((prevData) => {
      console.log(prevData);
      if (locale) {
        const ee = {
          ...prevData,
          data: {
            ...prevData.data,
            localeContent: {
              ...prevData.data?.localeContent,
              [locale]: {
                ...prevData.data?.localeContent?.[locale],
                [id]: value,
              },
            },
          },
        };

        console.log(ee);
        return ee;
      } else if (id == "identifier") {
        const ee = { ...prevData, [id]: value };
        return ee;
      }
      const ww = { ...prevData, data: { ...prevData.data, [id]: value } };
      console.log(ww);
      return ww;
    });
  };
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setErrorMessage("");
  };
  return loading ? (
    <Typography variant="h6" align="center">
      Loading Entry...
    </Typography>
  ) : (
    <>
      <Grid2 container spacing={2}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            maxWidth: 700,
            margin: "0 auto",
            padding: 3,
            boxShadow: 3,
            borderRadius: 2,
          }}
        >
          <Typography variant="h4" align="center" gutterBottom>
            {`Edit ${entry?.identifier} Entry`}
          </Typography>

          <Box mt={3} textAlign="center">
            <Button
              type="button"
              variant="contained"
              color="primary"
              size="large"
              onClick={() => handlePublish(!entry.published)}
            >
              {entry?.published ? `Un Publish` : "Publish"}
            </Button>
          </Box>

          <Grid2 container spacing={2}>
            <Grid2 size={12} key="identifier">
              <FormControl fullWidth variant="outlined" component="fieldset">
                <FormLabel component="legend">Identifier</FormLabel>
                <TextField
                  type="text"
                  variant="outlined"
                  fullWidth
                  // value={entry.identifier}
                  value={formData.identifier || ""}
                  onChange={(e) => handleChange("identifier", e.target.value)}
                />
              </FormControl>
            </Grid2>
            {data.map((field) => (
              <Grid2 size={12} key={field.id}>
                {renderInput(field)}
              </Grid2>
            ))}
          </Grid2>
          <Box mt={3} textAlign="center">
            <Button type="submit" variant="contained" color="primary" size="large">
              Submit
            </Button>
          </Box>

        </Box>
      </Grid2>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {severity === "success" ? successMessage : errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditEntry;
